import React from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";

function PaymentStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const details = location.state;

  if (!details) {
    return <Navigate to="/" replace />;
  }

  const { status, amount, number, order_id, operator_ref, cashback, message } = details;

  // Normalize status to match config keys
  const normalizeStatus = (status) => {
    const statusMap = {
      'Success': 'success',
      'success': 'success',
      'Processed': 'pending',
      'processed': 'pending',
      'Initiated': 'pending',
      'initiated': 'pending',
      'Pending': 'pending',
      'pending': 'pending',
      'Failed': 'failed',
      'failed': 'failed'
    };
    return statusMap[status] || 'pending';
  };

  const normalizedStatus = normalizeStatus(status);
  const isSuccess = normalizedStatus === 'success';
  const isFailed = normalizedStatus === 'failed';
  const isPending = normalizedStatus === 'pending';

  const config = {
    success: {
      color: APP_THEME.successColor,
      title: "Payment Successful!",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.successColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      bg: `${APP_THEME.successColor}15`,
      message: "Your recharge has been processed successfully."
    },
    failed: {
      color: APP_THEME.errorColor,
      title: "Payment Failed",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.errorColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      ),
      bg: `${APP_THEME.errorColor}15`,
      message: message || "Your payment could not be processed. Please try again."
    },
    pending: {
      color: APP_THEME.warningColor,
      title: "Payment Initiated",
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.warningColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      bg: `${APP_THEME.warningColor}15`,
      message: "Your payment is being processed. You will receive a confirmation shortly."
    }
  };

  const current = config[normalizedStatus];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8" style={{ background: APP_THEME.bgColor }}>
      <div className="w-full max-w-md">
        {/* Icon Circle */}
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ background: current.bg }}
        >
          {current.icon}
        </div>

        {/* Title */}
        <h2
          className="text-xl font-bold mb-1 text-center"
          style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
        >
          {current.title}
        </h2>

        {/* Message */}
        <p
          className="text-xs mb-6 text-center"
          style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
        >
          {current.message}
        </p>

        {/* Details Card */}
        <div
          className="rounded-2xl p-5 border mb-4"
          style={{
            background: APP_THEME.surfaceColor,
            borderColor: APP_THEME.borderColor,
            borderRadius: APP_THEME.borderRadiusCard,
          }}
        >
          {order_id && (
            <div className="mb-3 flex justify-between">
              <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Order ID</div>
              <div className="text-sm font-semibold text-right" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{order_id}</div>
            </div>
          )}

          {operator_ref && (
            <div className="mb-3 flex justify-between">
              <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Operator TXN/RRN</div>
              <div className="text-sm font-semibold text-right" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{operator_ref}</div>
            </div>
          )}

          <div className="mb-3 flex justify-between">
            <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Mobile Number</div>
            <div className="text-sm font-semibold text-right" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>{number}</div>
          </div>

          <div className="mb-3 flex justify-between">
            <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Amount</div>
            <div className="text-sm font-semibold text-right" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>₹{amount}</div>
          </div>

          {cashback && cashback > 0 && isSuccess && (
            <div
              className="mt-3 p-2.5 rounded-lg flex justify-between items-center"
              style={{
                background: `${APP_THEME.successColor}10`,
                border: `1px solid ${APP_THEME.successColor}20`
              }}
            >
              <div className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>Cashback Earned</div>
              <div className="text-sm font-semibold" style={{ color: APP_THEME.successColor, fontFamily: APP_THEME.fontFamily }}>₹{cashback}</div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/", { replace: true })}
            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all active:scale-98"
            style={{
              background: APP_THEME.surfaceColor,
              color: APP_THEME.textPrimary,
              border: `1px solid ${APP_THEME.borderColor}`,
              borderRadius: APP_THEME.borderRadiusBtn,
              fontFamily: APP_THEME.fontFamily,
            }}
          >
            Go Home
          </button>
          <button
            onClick={() => navigate("/history", { replace: true })}
            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all active:scale-98"
            style={{
              background: APP_THEME.primaryColor,
              color: APP_THEME.textOnPrimary,
              border: "none",
              borderRadius: APP_THEME.borderRadiusBtn,
              fontFamily: APP_THEME.fontFamily,
            }}
          >
            View History
          </button>
        </div>

        {/* Footer Messages */}
        {isFailed && (
          <p className="mt-6 text-xs text-center" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily, lineHeight: 1.5 }}>
            If money was deducted from your wallet, it will be refunded within 24 hours.
          </p>
        )}

        {isPending && (
          <p className="mt-6 text-xs text-center" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily, lineHeight: 1.5 }}>
            Your transaction is being processed. Please check back later for updates.
          </p>
        )}
      </div>

      <style>
        {`
          .active\\:scale-98:active {
            transform: scale(0.98);
          }
        `}
      </style>
    </div>
  );
}

export default PaymentStatus;